/**
 * iter366 — Espace client du lien de suivi : sa flotte (avec ajout d'un
 * véhicule), ses prochains rendez-vous, ses devis acceptés, et un bouton pour
 * demander un nouveau rendez-vous ou un devis. Chaque bloc dépend des
 * autorisations du lien.
 */
import { useState } from "react";
import axios from "axios";
import { Car, CalendarDays, FileCheck2, Plus, Send, Loader2, MessageCircle } from "lucide-react";

const jourFr = (iso) => {
  if (!iso) return "";
  const s = new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CH", {
    weekday: "long", day: "numeric", month: "long",
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const Bloc = ({ icone: Icone, titre, accent, children, testId }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <Icone className="w-4 h-4" style={{ color: accent }} />
      <h2 className="text-base font-semibold text-gray-900">{titre}</h2>
    </div>
    {children}
  </div>
);

export default function EspaceClient({ token, api, acces, data, vis, accent, onSaved }) {
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  const [veh, setVeh] = useState({ plaque: "", marque: "", modele: "", vin: "", premiere_mec: "", km_dernier: "" });
  const [demande, setDemande] = useState({ ouvert: false, nature: "rdv", vehicule_id: "", date_souhaitee: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const entetes = acces ? { headers: { "X-Suivi-Acces": acces } } : {};
  const clientId = (data.clients || [])[0]?.id;

  // iter368 — Lien vers Léa, déjà renseignée : aucune authentification, aucune
  // ressaisie, et le rendez-vous est pris pour de vrai (pas une demande à traiter).
  const lienLea = () => {
    const c = data.contact || {};
    const v = (data.flotte || []).find((x) => x.id === demande.vehicule_id)
      || ((data.flotte || []).length === 1 ? data.flotte[0] : null);
    const p = new URLSearchParams();
    if (c.prenom) p.set("prenom", c.prenom);
    if (c.nom) p.set("nom", c.nom);
    if (c.tel) p.set("tel", c.tel);
    if (c.email) p.set("email", c.email);
    if (v?.plaque) p.set("plaque", v.plaque);
    if (v?.marque) p.set("marque", v.marque);
    if (v?.modele) p.set("modele", v.modele);
    p.set("source_espace", "1");
    return `/widget/rdv-ia?${p.toString()}`;
  };

  const ajouterVehicule = async () => {
    if (!veh.plaque.trim() || !veh.marque.trim() || !veh.modele.trim()) {
      setMsg("Plaque, marque et modèle sont obligatoires."); return;
    }
    setBusy(true); setMsg("");
    try {
      await axios.post(`${api}/public/suivi/${token}/vehicules`, {
        ...veh,
        km_dernier: veh.km_dernier ? parseInt(veh.km_dernier, 10) : null,
        client_id: clientId,
      }, entetes);
      setVeh({ plaque: "", marque: "", modele: "", vin: "", premiere_mec: "", km_dernier: "" });
      setAjoutOuvert(false);
      await onSaved?.();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Ajout impossible");
    } finally { setBusy(false); }
  };

  const envoyerDemande = async () => {
    if (!demande.message.trim()) { setMsg("Décrivez votre demande."); return; }
    setBusy(true); setMsg("");
    try {
      await axios.post(`${api}/public/suivi/${token}/demande`, {
        nature: demande.nature,
        vehicule_id: demande.vehicule_id || null,
        date_souhaitee: demande.date_souhaitee || null,
        message: demande.message,
        client_id: clientId,
      }, entetes);
      setDemande({ ouvert: false, nature: "rdv", vehicule_id: "", date_souhaitee: "", message: "" });
      setMsg("Demande envoyée — le garage vous recontacte.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Envoi impossible");
    } finally { setBusy(false); }
  };

  const champ = (label, props, testid) => (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-gray-500">{label}</span>
      <input
        className="w-full h-11 px-3 rounded-lg border border-gray-200 text-sm mt-0.5"
        data-testid={testid} {...props}
      />
    </label>
  );

  return (
    <div className="space-y-3 mt-4" data-testid="suivi-espace-client">
      {vis.flotte && (
        <Bloc icone={Car} titre="Mes véhicules" accent={accent} testId="suivi-flotte">
          <div className="space-y-1.5">
            {(data.flotte || []).length === 0 && (
              <p className="text-sm text-gray-500" data-testid="suivi-flotte-vide">
                Aucun véhicule enregistré pour l&apos;instant.
              </p>
            )}
            {(data.flotte || []).map((v) => (
              <div
                key={v.id}
                className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 bg-gray-50/60"
                data-testid={`suivi-flotte-${v.id}`}
              >
                <span className="font-mono text-xs font-bold text-gray-800 w-28 flex-shrink-0">{v.plaque}</span>
                <span className="text-sm text-gray-700 flex-1 truncate">
                  {`${v.marque || ""} ${v.modele || ""}`.trim()}
                </span>
                {v.km_dernier ? (
                  <span className="text-[11px] text-gray-500">{v.km_dernier.toLocaleString("fr-CH")} km</span>
                ) : null}
              </div>
            ))}
          </div>

          {vis.flotte_ajout && !ajoutOuvert && (
            <button
              type="button"
              onClick={() => setAjoutOuvert(true)}
              className="mt-3 w-full h-11 rounded-lg border border-dashed border-gray-300 text-sm text-gray-600 hover:border-gray-400 flex items-center justify-center gap-1.5"
              data-testid="suivi-flotte-ajouter"
            >
              <Plus className="w-4 h-4" /> Ajouter un véhicule
            </button>
          )}

          {vis.flotte_ajout && ajoutOuvert && (
            <div className="mt-3 space-y-2 border-t border-gray-100 pt-3" data-testid="suivi-flotte-form">
              {champ("Plaque *", {
                value: veh.plaque, placeholder: "GE 123456",
                onChange: (e) => setVeh({ ...veh, plaque: e.target.value }),
              }, "suivi-veh-plaque")}
              <div className="grid grid-cols-2 gap-2">
                {champ("Marque *", {
                  value: veh.marque, onChange: (e) => setVeh({ ...veh, marque: e.target.value }),
                }, "suivi-veh-marque")}
                {champ("Modèle *", {
                  value: veh.modele, onChange: (e) => setVeh({ ...veh, modele: e.target.value }),
                }, "suivi-veh-modele")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {champ("1re mise en circulation", {
                  type: "date", value: veh.premiere_mec,
                  onChange: (e) => setVeh({ ...veh, premiere_mec: e.target.value }),
                }, "suivi-veh-mec")}
                {champ("Kilométrage", {
                  type: "number", inputMode: "numeric", value: veh.km_dernier,
                  onChange: (e) => setVeh({ ...veh, km_dernier: e.target.value }),
                }, "suivi-veh-km")}
              </div>
              {champ("Numéro de châssis (VIN)", {
                value: veh.vin, onChange: (e) => setVeh({ ...veh, vin: e.target.value }),
              }, "suivi-veh-vin")}
              <div className="flex gap-2 pt-1">
                <button
                  type="button" onClick={() => setAjoutOuvert(false)}
                  className="flex-1 h-11 rounded-lg border border-gray-200 text-sm text-gray-600"
                  data-testid="suivi-veh-annuler"
                >
                  Annuler
                </button>
                <button
                  type="button" onClick={ajouterVehicule} disabled={busy}
                  className="flex-1 h-11 rounded-lg text-white text-sm font-semibold flex items-center justify-center"
                  style={{ backgroundColor: accent }}
                  data-testid="suivi-veh-enregistrer"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
                </button>
              </div>
            </div>
          )}
        </Bloc>
      )}

      {vis.rdv && (
        <Bloc icone={CalendarDays} titre="Mes prochains rendez-vous" accent={accent} testId="suivi-rdv">
          {(data.rdv || []).length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="suivi-rdv-vide">Aucun rendez-vous à venir.</p>
          ) : (
            <div className="space-y-1.5">
              {data.rdv.map((r) => (
                <div
                  key={r.id}
                  className="p-2.5 rounded-lg border border-gray-100 bg-gray-50/60"
                  data-testid={`suivi-rdv-${r.id}`}
                >
                  <div className="text-sm font-medium text-gray-800">
                    {jourFr(r.date)}{r.heure_debut ? ` · ${r.heure_debut}` : ""}
                  </div>
                  <div className="text-xs text-gray-600">
                    {[r.vehicule_label, r.type_intervention_nom].filter(Boolean).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Bloc>
      )}

      {vis.devis_acceptes && (
        <Bloc icone={FileCheck2} titre="Mes devis acceptés" accent={accent} testId="suivi-devis-acceptes">
          {(data.devis_acceptes || []).length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="suivi-devis-acceptes-vide">
              Aucun devis accepté pour l&apos;instant.
            </p>
          ) : (
            <div className="space-y-1.5">
              {data.devis_acceptes.map((d) => (
                <a
                  key={d.id}
                  href={d.token_public ? `/devis/${d.token_public}` : undefined}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 bg-gray-50/60 hover:border-gray-300"
                  data-testid={`suivi-devis-accepte-${d.id}`}
                >
                  <span className="font-mono text-xs font-bold text-gray-800">{d.numero}</span>
                  <span className="text-sm text-gray-700 flex-1 truncate">
                    {d.titre || d.vehicule_label || ""}
                  </span>
                  <span className="text-sm font-semibold" style={{ color: accent }}>
                    {(d.montant_ttc || 0).toFixed(2)} CHF
                  </span>
                </a>
              ))}
            </div>
          )}
        </Bloc>
      )}

      {vis.demandes && (
        <Bloc icone={Send} titre="Une nouvelle demande ?" accent={accent} testId="suivi-demandes">
          {!demande.ouvert ? (
            <>
              {(data.flotte || []).length > 1 && (
                <label className="block mb-2">
                  <span className="text-[11px] uppercase tracking-wider text-gray-500">Véhicule concerné</span>
                  <select
                    value={demande.vehicule_id}
                    onChange={(e) => setDemande({ ...demande, vehicule_id: e.target.value })}
                    className="w-full h-11 px-3 rounded-lg border border-gray-200 text-sm mt-0.5 bg-white"
                    data-testid="suivi-demande-vehicule-pre"
                  >
                    <option value="">— Choisir —</option>
                    {data.flotte.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.plaque} · {`${v.marque || ""} ${v.modele || ""}`.trim()}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="grid grid-cols-2 gap-2">
                {/* iter368 — Rendez-vous : Léa prend le rendez-vous directement,
                    sans authentification ni ressaisie. */}
                <a
                  href={lienLea()}
                  target="_blank" rel="noopener noreferrer"
                  className="h-11 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-1.5"
                  style={{ backgroundColor: accent }}
                  data-testid="suivi-demande-rdv"
                >
                  <MessageCircle className="w-4 h-4" /> Prendre rendez-vous
                </a>
                <button
                  type="button"
                  onClick={() => setDemande({ ...demande, ouvert: true, nature: "devis" })}
                  className="h-11 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700"
                  data-testid="suivi-demande-devis"
                >
                  Demander un devis
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                Léa, notre assistante, vous propose les créneaux disponibles en quelques messages.
              </p>
            </>
          ) : (
            <div className="space-y-2" data-testid="suivi-demande-form">
              <div className="text-xs text-gray-500">
                {demande.nature === "devis" ? "Demande de devis" : "Demande de rendez-vous"}
              </div>
              {(data.flotte || []).length > 0 && (
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-gray-500">Véhicule</span>
                  <select
                    value={demande.vehicule_id}
                    onChange={(e) => setDemande({ ...demande, vehicule_id: e.target.value })}
                    className="w-full h-11 px-3 rounded-lg border border-gray-200 text-sm mt-0.5 bg-white"
                    data-testid="suivi-demande-vehicule"
                  >
                    <option value="">— Choisir —</option>
                    {data.flotte.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.plaque} · {`${v.marque || ""} ${v.modele || ""}`.trim()}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {demande.nature === "rdv" && champ("Date souhaitée", {
                type: "date", value: demande.date_souhaitee,
                onChange: (e) => setDemande({ ...demande, date_souhaitee: e.target.value }),
              }, "suivi-demande-date")}
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-gray-500">Votre demande *</span>
                <textarea
                  rows={3}
                  value={demande.message}
                  onChange={(e) => setDemande({ ...demande, message: e.target.value })}
                  placeholder="Ex. bruit de freins à l'avant, service annuel…"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm mt-0.5 resize-none"
                  data-testid="suivi-demande-message"
                />
              </label>
              <div className="flex gap-2">
                <button
                  type="button" onClick={() => setDemande({ ...demande, ouvert: false })}
                  className="flex-1 h-11 rounded-lg border border-gray-200 text-sm text-gray-600"
                  data-testid="suivi-demande-annuler"
                >
                  Annuler
                </button>
                <button
                  type="button" onClick={envoyerDemande} disabled={busy}
                  className="flex-1 h-11 rounded-lg text-white text-sm font-semibold flex items-center justify-center"
                  style={{ backgroundColor: accent }}
                  data-testid="suivi-demande-envoyer"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer"}
                </button>
              </div>
            </div>
          )}
        </Bloc>
      )}

      {msg && (
        <p className="text-sm text-center text-gray-700" data-testid="suivi-espace-message">{msg}</p>
      )}
    </div>
  );
}
